import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { VenueFinancesService } from 'app/modules/sho_management_pro/venue/services/venue-finances.service';
import { AlertService } from 'app/services/alert.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { AdapterService } from '../../services/adapters/adapter.service';
import { OtherVendorsSalesService } from '../../services/sales/box-office/other-vendors-sales.service';
import * as OtherVendorsSalesActions from './other-vendors-sales.actions';

@Injectable()
export class OtherVendorsSalesEffects {
    private _actions$ = inject(Actions);
    private _otherVendorsSalesService = inject(OtherVendorsSalesService);
    private adapter = inject(AdapterService);
    private _venueFinancesService = inject(VenueFinancesService);
    private _alertService = inject(AlertService);

    messages = {
        getAllError: 'Error getting all other vendors sales',
        getOneError: 'Error getting other vendor sale',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All OtherVendorsSales
    getAllOtherVendorsSales$ = createEffect(() =>
        this._actions$.pipe(
            ofType(OtherVendorsSalesActions.loadOtherVendorsSales),
            switchMap((action) => {
                return this._otherVendorsSalesService
                    .getAll(action.playdateId)
                    .pipe(
                        map((res) => {
                            const sales =
                                this.adapter.adaptOtherVendorsSalesList(
                                    res.data
                                );

                            return OtherVendorsSalesActions.loadOtherVendorsSalesSuccess(
                                {
                                    otherVendorsSales: sales,
                                }
                            );
                        }),
                        catchError((error) =>
                            of(
                                OtherVendorsSalesActions.loadOtherVendorsSalesFailure(
                                    {
                                        error:
                                            error?.error?.message ||
                                            this.messages.unknownError,
                                    }
                                )
                            )
                        )
                    );
            })
        )
    );

    // Get One OtherVendorsSale
    getOtherVendorsSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(OtherVendorsSalesActions.loadOtherVendorsSale),
            switchMap((action) => {
                return this._otherVendorsSalesService.getOne(action.id).pipe(
                    map((res) => {
                        return OtherVendorsSalesActions.loadOtherVendorsSaleSuccess(
                            {
                                otherVendorsSale: res.data,
                            }
                        );
                    }),
                    catchError((error) =>
                        of(
                            OtherVendorsSalesActions.loadOtherVendorsSaleFailure(
                                {
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                }
                            )
                        )
                    )
                );
            })
        )
    );

    // Create OtherVendorsSale
    createOtherVendorsSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(OtherVendorsSalesActions.createOtherVendorsSale),
            switchMap((action) => {
                return this._otherVendorsSalesService.create(action.data).pipe(
                    map((res) => {
                        const data = res.data;
                        const sale = this.adapter.adaptOtherVendorsSale(data);

                        if (!sale) {
                            throw new Error(this.messages.getOneError);
                        }

                        return OtherVendorsSalesActions.createOtherVendorsSaleSuccess(
                            {
                                otherVendorsSale: sale,
                                success:
                                    'Other vendors sale created successfully',
                            }
                        );
                    }),
                    catchError((error) =>
                        of(
                            OtherVendorsSalesActions.createOtherVendorsSaleFailure(
                                {
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                }
                            )
                        )
                    )
                );
            })
        )
    );

    // Update OtherVendorsSale
    updateOtherVendorsSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(OtherVendorsSalesActions.updateOtherVendorsSale),
            switchMap((action) => {
                return this._otherVendorsSalesService.update(action.data).pipe(
                    map((res) => {
                        const data = res.data;
                        const sale = this.adapter.adaptOtherVendorsSale(data);

                        if (!sale) {
                            throw new Error(this.messages.getOneError);
                        }

                        return OtherVendorsSalesActions.updateOtherVendorsSaleSuccess(
                            {
                                otherVendorsSale: sale,
                                success:
                                    'Other vendors sale updated successfully',
                            }
                        );
                    }),
                    catchError((error) =>
                        of(
                            OtherVendorsSalesActions.updateOtherVendorsSaleFailure(
                                {
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                }
                            )
                        )
                    )
                );
            })
        )
    );

    // Delete Other Vendors Sale
    deleteOtherVendorsSale$ = createEffect(() =>
        this._actions$.pipe(
            ofType(OtherVendorsSalesActions.deleteOtherVendorsSale),
            switchMap((action) => {
                return this._otherVendorsSalesService
                    .delete(action.payload)
                    .pipe(
                        // tap((res) => console.log(res)), // Debug
                        map((res) => {
                            const deletedSales = res.data.deleted_sales;
                            return OtherVendorsSalesActions.deleteOtherVendorsSaleSuccess(
                                {
                                    deletedSales: deletedSales,
                                    success:
                                        'Other vendors sale deleted successfully',
                                }
                            );
                        }),
                        catchError((error) =>
                            of(
                                OtherVendorsSalesActions.deleteOtherVendorsSaleFailure(
                                    {
                                        error:
                                            error?.error?.message ||
                                            this.messages.unknownError,
                                    }
                                )
                            )
                        )
                    );
            })
        )
    );

    // Delete Other Vendors Sale Item
    deleteOtherVendorsSaleItem$ = createEffect(() =>
        this._actions$.pipe(
            ofType(OtherVendorsSalesActions.deleteOtherVendorsSaleItem),
            switchMap((action) => {
                return this._otherVendorsSalesService
                    .delete(action.payload)
                    .pipe(
                        // tap((res) => console.log(res)), // Debug
                        map((res) => {
                            const saleId = res.data.deleted_sales[0];

                            if (!saleId) {
                                const message =
                                    'Sale item ID not found in response';
                                this._alertService.showAlert(message, 'error');
                                throw new Error(message);
                            }

                            return OtherVendorsSalesActions.deleteOtherVendorsSaleItemSuccess(
                                {
                                    deletedSaleItem: saleId,
                                    success:
                                        'Other vendors sale item deleted successfully',
                                }
                            );
                        }),
                        catchError((error) =>
                            of(
                                OtherVendorsSalesActions.deleteOtherVendorsSaleItemFailure(
                                    {
                                        error:
                                            error?.error?.message ||
                                            this.messages.unknownError,
                                    }
                                )
                            )
                        )
                    );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onOtherVendorsSaleActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    OtherVendorsSalesActions.createOtherVendorsSaleSuccess,
                    OtherVendorsSalesActions.updateOtherVendorsSaleSuccess,
                    OtherVendorsSalesActions.deleteOtherVendorsSaleSuccess,
                    OtherVendorsSalesActions.deleteOtherVendorsSaleItemSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');

                    // Reload totals
                    this._venueFinancesService.onUpdateFinances = true;
                })
            ),
        { dispatch: false }
    );

    onOtherVendorsSaleActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    OtherVendorsSalesActions.loadOtherVendorsSalesFailure,
                    OtherVendorsSalesActions.loadOtherVendorsSaleFailure,
                    OtherVendorsSalesActions.createOtherVendorsSaleFailure,
                    OtherVendorsSalesActions.updateOtherVendorsSaleFailure,
                    OtherVendorsSalesActions.deleteOtherVendorsSaleFailure,
                    OtherVendorsSalesActions.deleteOtherVendorsSaleItemFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
